<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { block, field, patch } = useInlineBlockEditor(props.id);

const assessmentId = field<string>("assessmentId", "");
const individualReportPath = field<string>("individualReportPath", "");
const sortColumn = field<string>("sortColumn", "");
const overallConstructId = field<string>("overallConstructId", "");
const overallConstructLabel = field<string>("overallConstructLabel", "");
const showStarted = field<boolean>("showStarted", false);
const showCompleted = field<boolean>("showCompleted", false);
const showAnswerCount = field<boolean>("showAnswerCount", false);

const selectedColumns = computed({
  get: () => ((block.value?.selectedColumns ?? []) as string[]).join(", "),
  set: (value: string) => {
    patch({ selectedColumns: value.split(",").map((item) => item.trim()).filter(Boolean) });
  },
});

const sortAscending = computed({
  get: () => (block.value?.sortOrder ?? "desc") === "asc",
  set: (value: boolean) => patch({ sortOrder: value ? "asc" : "desc" }),
});
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:file-chart-column-increasing" accent="reporting">
    <EditorField :label="t('assessmentIdLabel')">
      <Input v-model="assessmentId" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <EditorField :label="t('individualReportPathLabel')">
      <EditorPathInput
        v-model="individualReportPath"
        mode="existing-file"
        :scopes="['site']"
        content-type-filter="report"
        placeholder="/reports/individual"
        aria-label="Individual report path"
      />
    </EditorField>

    <EditorField :label="t('columnsLabel')" :hint="t('columnsHint')">
      <Input v-model="selectedColumns" class="h-8 rounded-sm bg-muted text-[13px]" placeholder="OVERALL SUITABILITY, OVERALL POTENTIAL" />
    </EditorField>

    <EditorField :label="t('sortColumnLabel')">
      <Input v-model="sortColumn" class="h-8 rounded-sm bg-muted text-[13px]" />
    </EditorField>

    <EditorField :label="t('ascendingLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("ascendingHint") }}</span>
        <Switch v-model="sortAscending" />
      </div>
    </EditorField>

    <EditorField :label="t('overallConstructIdLabel')" :hint="t('overallConstructIdHelp')">
      <Input v-model="overallConstructId" class="h-8 rounded-sm bg-muted text-[13px]" placeholder="101" />
    </EditorField>

    <EditorField :label="t('overallConstructLabelLabel')" :hint="t('overallConstructLabelHelp')">
      <Input v-model="overallConstructLabel" class="h-8 rounded-sm bg-muted text-[13px]" placeholder="Overall Suitability" />
    </EditorField>

    <EditorField :label="t('timelineColumnsLabel')">
      <div class="space-y-2">
        <label class="flex items-center justify-between gap-3 text-[12.5px] text-muted-foreground">
          <span>{{ t("showStartedLabel") }}</span>
          <Checkbox v-model:checked="showStarted" />
        </label>
        <label class="flex items-center justify-between gap-3 text-[12.5px] text-muted-foreground">
          <span>{{ t("showCompletedLabel") }}</span>
          <Checkbox v-model:checked="showCompleted" />
        </label>
        <label class="flex items-center justify-between gap-3 text-[12.5px] text-muted-foreground">
          <span>{{ t("showAnswerCountLabel") }}</span>
          <Checkbox v-model:checked="showAnswerCount" />
        </label>
      </div>
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Ranked Report"
  assessmentIdLabel: "Assessment ID"
  individualReportPathLabel: "Individual Report Path"
  columnsLabel: "Selected Columns"
  columnsHint: "Comma-separated report fields to display."
  sortColumnLabel: "Sort Column"
  ascendingLabel: "Ascending"
  ascendingHint: "Sort from lowest to highest."
  overallConstructIdLabel: "Overall Construct ID"
  overallConstructIdHelp: "Optional construct ID to display and sort as the overall column."
  overallConstructLabelLabel: "Overall Column Label"
  overallConstructLabelHelp: "Optional override for the overall column header."
  timelineColumnsLabel: "Assessment Timeline Columns"
  showStartedLabel: "Started"
  showCompletedLabel: "Completed"
  showAnswerCountLabel: "Answer Count"
</i18n>
